import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { useEffect, useMemo } from 'react';
import { useExecuteOnce } from '@/shared/lib';
import type { RegistrationInfoResponseDto } from '@/features/auth-signup';
import type { Country } from '@/shared/types';
import { parsePhoneNumber, resolvePhoneCode } from '@/shared/lib/phone';
import { createPhoneFormSchema } from './createPhoneFormSchema';

export type PhoneFormValues = z.infer<ReturnType<typeof createPhoneFormSchema>>;

const usePhoneForm = (
  countriesData: Country[] | undefined,
  registrationInfo: Partial<RegistrationInfoResponseDto> | undefined
) => {
  const { t } = useTranslation();

  const schema = createPhoneFormSchema(t);

  const form = useForm<PhoneFormValues>({
    mode: 'onSubmit',
    resolver: zodResolver(schema),
    defaultValues: {
      countryCode: '',
      phoneNumber: '',
    },
  });

  const defaultValues = useMemo(() => {
    if (countriesData && registrationInfo && registrationInfo.phone) {
      const { phoneCode, phoneNumber } = parsePhoneNumber(
        registrationInfo.phone,
        countriesData
      );
      return { countryCode: phoneCode, phoneNumber };
    }

    const phoneCode = resolvePhoneCode(countriesData, registrationInfo);
    return { countryCode: phoneCode, phoneNumber: '' };
  }, [countriesData, registrationInfo]);

  useExecuteOnce(() => {
    if (defaultValues) {
      form.reset(defaultValues);
    }
  }, !!countriesData && !!registrationInfo);

  useEffect(() => {
    if (form.formState.isSubmitted) {
      form.trigger();
    }
  }, [t]);

  return {
    form,
  };
};

export { usePhoneForm };
